import { Injectable } from '@angular/core';

export interface FlattenOptions {
  /**
   * Separator to use between nested keys (default: '.')
   * Example: 'personalInfo.name' vs 'personalInfo_name'
   */
  separator?: string;
  
  /**
   * Whether to use only the final field names without parent keys (default: false)
   * Example: 'name' instead of 'personalInfo.name'
   */
  useOriginalFieldNamesOnly?: boolean;
  
  /**
   * Whether to include null values in the flattened result (default: false)
   */
  includeNullValues?: boolean;
  
  /**
   * Whether to include undefined values in the flattened result (default: false)
   */
  includeUndefinedValues?: boolean;
  
  /**
   * Custom prefix to add to all keys (default: '')
   */
  prefix?: string;
  
  /**
   * Whether to treat form as fieldset-based structure (default: false)
   * When true, treats first level keys as fieldset names and flattens accordingly
   * Example: { personalInfo: { name: 'John' } } becomes { 'personalInfo.name': 'John' }
   * When false with useOriginalFieldNamesOnly: true, becomes { 'name': 'John' }
   */
  isFieldsetForm?: boolean;
  
  /**
   * Array of fieldset names to exclude from flattening (default: [])
   * Only applies when isFieldsetForm is true
   */
  excludeFieldsets?: string[];
  
  /**
   * Whether to preserve fieldset names in keys even with useOriginalFieldNamesOnly (default: false)
   * Only applies when isFieldsetForm is true
   * Example: { personalInfo: { name: 'John' } } becomes { 'personalInfo.name': 'John' } instead of { 'name': 'John' }
   */
  preserveFieldsetNames?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FormUtilityService {

  constructor() { }

  /**
   * Flattens a nested form value object into a flat key-value structure
   * @param formValue The nested form value object to flatten
   * @param options Configuration options for flattening behavior
   * @returns Flattened object with dot-notation keys
   */
  flattenFormValue(formValue: any, options: FlattenOptions = {}): any {
    const {
      separator = '.',
      useOriginalFieldNamesOnly = false,
      includeNullValues = false,
      includeUndefinedValues = false,
      prefix = '',
      isFieldsetForm = false,
      excludeFieldsets = [],
      preserveFieldsetNames = false
    } = options;

    if (isFieldsetForm) {
      return this.flattenFieldsetForm(formValue, options);
    }

    if (useOriginalFieldNamesOnly) {
      return this.flattenToOriginalFieldNames(formValue, includeNullValues, includeUndefinedValues);
    }

    return this.flattenRecursively(formValue, prefix, separator, includeNullValues, includeUndefinedValues);
  }

  /**
   * Creates a FormData object from a form value, with automatic flattening
   * @param formValue The form value to convert
   * @param options Flattening options
   * @returns FormData object ready for HTTP submission
   */
  createFormDataFromValue(formValue: any, options: FlattenOptions = {}): FormData {
    const flattenedValue = this.flattenFormValue(formValue, options);
    let formData = new FormData();

    for (let [key, value] of Object.entries(flattenedValue)) {
      if (this.shouldIncludeValue(value, options)) {
        console.log(`Appending key '${key}' with value:`, value);
        this.appendToFormData(formData, key, value);
      }else{
        console.log(`Skipping value for key '${key}' due to options:`, {
          includeNullValues: options.includeNullValues,
          includeUndefinedValues: options.includeUndefinedValues
        })
      }
    }
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    formData = this.removeEmptyValues(formData);
    return formData;
  }
  //Ensure that only keys with values are returned
  removeEmptyValues(originalFormData: FormData): FormData {
    const cleanedFormData = new FormData();
  
    originalFormData.forEach((value, key) => {
      // Handle both string and File values
      const isEmpty =
        value === '' ||
        value === null ||
        value === undefined ||
        (value instanceof File && value.size === 0 && value.name === '');
  
      if (!isEmpty) {
        cleanedFormData.append(key, value);
      }
    });
  
    return cleanedFormData;
  }

  /**
   * Converts a flattened object to FormData
   * @param flattenedObject Already flattened key-value object
   * @returns FormData object
   */
  objectToFormData(flattenedObject: any): FormData {
    const formData = new FormData();
    
    for (let [key, value] of Object.entries(flattenedObject)) {
      if (value !== null && value !== undefined) {
        this.appendToFormData(formData, key, value);
      }
    }

    return formData;
  }

  /**
   * Validates if a form value structure is nested and needs flattening
   * @param formValue The form value to check
   * @returns True if the form value contains nested objects
   */
  isNestedFormValue(formValue: any): boolean {
    if (!formValue || typeof formValue !== 'object') {
      return false;
    }

    return Object.values(formValue).some(value => 
      value !== null && 
      typeof value === 'object' && 
      !(value instanceof File) && 
      !(value instanceof Date) &&
      !(value instanceof Array)
    );
  }

  /**
   * Gets all form field names from a nested form structure
   * @param formValue The nested form value
   * @param options Flattening options
   * @returns Array of field names
   */
  getFormFieldNames(formValue: any, options: FlattenOptions = {}): string[] {
    const flattened = this.flattenFormValue(formValue, options);
    return Object.keys(flattened);
  }

  /**
   * Gets fieldset names from a fieldset-based form
   * @param formValue The form value with fieldsets
   * @returns Array of fieldset names
   */
  getFieldsetNames(formValue: any): string[] {
    if (!formValue || typeof formValue !== 'object') {
      return [];
    }
    return Object.keys(formValue).filter(key => 
      formValue[key] && typeof formValue[key] === 'object'
    );
  }

  /**
   * Gets field names within a specific fieldset
   * @param formValue The form value
   * @param fieldsetName The name of the fieldset
   * @returns Array of field names within the fieldset
   */
  getFieldsetFieldNames(formValue: any, fieldsetName: string): string[] {
    if (!formValue || !formValue[fieldsetName] || typeof formValue[fieldsetName] !== 'object') {
      return [];
    }
    return Object.keys(formValue[fieldsetName]);
  }

  /**
   * Flattens a fieldset-based form structure
   */
  private flattenFieldsetForm(formValue: any, options: FlattenOptions): any {
    const {
      separator = '.',
      useOriginalFieldNamesOnly = false,
      includeNullValues = false,
      includeUndefinedValues = false,
      prefix = '',
      excludeFieldsets = [],
      preserveFieldsetNames = false
    } = options;

    const result = {};
    console.log(formValue,options)
    for (let fieldsetName in formValue) {
      console.log(fieldsetName)
      if (formValue.hasOwnProperty(fieldsetName)) {
        // Skip excluded fieldsets
        if (excludeFieldsets.includes(fieldsetName)) {
          continue;
        }

        const fieldsetValue = formValue[fieldsetName];
        
        if (fieldsetValue && typeof fieldsetValue === 'object') {
          console.log({fieldsetName, fieldsetValue})
          for (let fieldName in fieldsetValue) {
            if (fieldsetValue.hasOwnProperty(fieldName)) {
              const value = fieldsetValue[fieldName];
              console.log({fieldName, value})
              if (this.shouldIncludeValueInFlattening(value, includeNullValues, includeUndefinedValues)) {
                let key: string;
                
                if (useOriginalFieldNamesOnly && !preserveFieldsetNames) {
                  // Use only the field name: 'name'
                  key = prefix ? `${prefix}${separator}${fieldName}` : fieldName;
                  console.log({key: key})
                } else {
                  // Use fieldset.field format: 'personalInfo.name'
                  const baseKey = `${fieldsetName}${separator}${fieldName}`;
                  key = prefix ? `${prefix}${separator}${baseKey}` : baseKey;
                }
                
                // Handle nested objects within fields
                if (this.shouldFlattenValue(value)) {
                  console.log(`Flattening nested value for key '${key}'`, value);
                  const nestedFlattened = this.flattenRecursively(
                    value, 
                    key, 
                    separator, 
                    includeNullValues, 
                    includeUndefinedValues
                  );
                  Object.assign(result, nestedFlattened);
                } else {
                  console.log(`Adding value for key '${key}':`, value);
                  result[key] = value;
                }
              }else{
                console.warn(`Skipping value for key '${fieldsetName}${separator}${fieldName}' due to options:`, {
                  includeNullValues,
                  includeUndefinedValues
                });
              }
            }
          }
        } else if (this.shouldIncludeValueInFlattening(fieldsetValue, includeNullValues, includeUndefinedValues)) {
          // Handle case where fieldset itself has a primitive value
          const key = prefix ? `${prefix}${separator}${fieldsetName}` : fieldsetName;
          result[key] = fieldsetValue;
        }
      }
    }
    console.log(result)
    return result;
  }
  private flattenRecursively(
    obj: any, 
    parentKey: string = '', 
    separator: string = '.', 
    includeNull: boolean = false, 
    includeUndefined: boolean = false,
    result: any = {}
  ): any {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = parentKey ? `${parentKey}${separator}${key}` : key;
        const value = obj[key];
        
        if (this.shouldFlattenValue(value)) {
          this.flattenRecursively(value, newKey, separator, includeNull, includeUndefined, result);
        } else if (this.shouldIncludeValueInFlattening(value, includeNull, includeUndefined)) {
          result[newKey] = value;
        }
      }
    }
    return result;
  }

  /**
   * Flattens to use only original field names (useful when backend expects specific field names)
   */
  private flattenToOriginalFieldNames(
    formValue: any, 
    includeNull: boolean = false, 
    includeUndefined: boolean = false
  ): any {
    const result = {};
    
    for (let fieldSetKey in formValue) {
      if (formValue.hasOwnProperty(fieldSetKey)) {
        const fieldSetValue = formValue[fieldSetKey];
        
        if (fieldSetValue && typeof fieldSetValue === 'object') {
          for (let fieldKey in fieldSetValue) {
            if (fieldSetValue.hasOwnProperty(fieldKey)) {
              const value = fieldSetValue[fieldKey];
              if (this.shouldIncludeValueInFlattening(value, includeNull, includeUndefined)) {
                result[fieldKey] = value;
              }
            }
          }
        }
      }
    }
    
    return result;
  }

  /**
   * Determines if a value should be flattened further
   */
  private shouldFlattenValue(value: any): boolean {
    console.log(value);
    return value !== null && 
           typeof value === 'object' && 
           !(value instanceof File) && 
           !(value instanceof Date) &&
           !(value instanceof Array);
  }

  /**
   * Determines if a value should be included in the flattened result
   */
  private shouldIncludeValueInFlattening(value: any, includeNull: boolean, includeUndefined: boolean): boolean {
    if (value === null) return includeNull;
    if (value === undefined) return includeUndefined;
    return true;
  }

  /**
   * Determines if a value should be included based on options
   */
  private shouldIncludeValue(value: any, options: FlattenOptions): boolean {
    console.log({value, options});
    if (value === null && !options.includeNullValues) return false;
    if (value === undefined && !options.includeUndefinedValues) return false;
    return true;
  }

  /**
   * Properly appends different value types to FormData
   */
  private appendToFormData(formData: FormData, key: string, value: any): void {
    if (value instanceof File) {
      formData.append(key, value);
    } else if (value instanceof Date) {
      formData.append(key, value.toISOString());
    } else if (typeof value === 'object' && value !== null) {
      formData.append(key, JSON.stringify(value));
    } else if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
    console.log(`Appended key '${key}' with value:`, value);
  }
}